import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { SiteShell } from "@/components/site-shell";
import { useAuth } from "@/hooks/use-auth";
import { createCheckoutSession } from "@/lib/stripe.functions";
import { Button } from "@/components/ui/button";
import { GenerateCta } from "@/components/generate-cta";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";

export const FAQS = [
  {
    q: "Is DealDraft really free?",
    a: "Yes. The free tier gives you 3 generations per month — each one produces a complete proposal AND contract. No credit card required to start. Free documents aren't saved; upgrade to Pro for unlimited generations and storage.",
  },
  {
    q: "Are these contracts legally binding?",
    a: "Yes, when properly signed by both parties, these contracts are legally enforceable service agreements covering payment, deliverables, intellectual property, and project cancellation. For projects over $5,000, we recommend a quick review by a local attorney. DealDraft generates the framework — you ensure it fits your specific situation.",
  },
  {
    q: "Can I edit the proposal or contract before sending?",
    a: "Yes. After generation, both documents are fully editable in the app before you download them. Make any changes you need to match your voice and specific project requirements.",
  },
  {
    q: "What freelance disciplines does DealDraft support?",
    a: "Web design, graphic design, copywriting, photography, video production, software development, consulting, and social media management. The AI tailors the language and contract clauses specifically to your discipline.",
  },
  {
    q: "What happens to my documents after I generate them?",
    a: "Free users can download immediately but documents aren't saved to a dashboard. Pro users get full history — every proposal and contract saved, searchable, and re-downloadable at any time.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "DealDraft — Free AI Proposals & Contracts for Freelancers" },
      {
        name: "description",
        content:
          "Create professional proposals and contracts in 60 seconds with AI. Free to start — no credit card. Built for freelance designers, writers, and consultants.",
      },
      { property: "og:title", content: "DealDraft — AI Proposal & Contract Generator" },
      {
        property: "og:description",
        content: "Professional proposals and contracts in 60 seconds. Free to start.",
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "DealDraft",
          url: "https://dealdraft.pro",
          logo: "https://dealdraft.pro/favicon-512.svg",
          founder: {
            "@type": "Person",
            name: "Jerame Maupin",
            sameAs: [
              "https://www.linkedin.com/in/jerame-maupin-83a97a373",
              "https://x.com/JerameMaupin",
              "https://github.com/jerame35",
            ],
          },
        },
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "DealDraft",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description: "AI-powered proposal and contract generator for freelancers. Free to start.",
          offers: [
            { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
            { "@type": "Offer", name: "Pro", price: "19", priceCurrency: "USD" },
          ],
        },
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
    ],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/" }],
  }),
  component: Index,
});

const STEPS = [
  {
    emoji: "📋",
    title: "Fill the Form",
    text: "Enter your project details — client name, scope, timeline, price. 10 fields, under 2 minutes.",
  },
  {
    emoji: "⚡",
    title: "AI Generates Both Documents",
    text: "DealDraft writes a complete proposal AND a legally-sound contract tailored to your exact project. In seconds.",
  },
  {
    emoji: "📤",
    title: "Download & Send",
    text: "Export as PDF with one click. Send to your client looking like you've been doing this for years.",
  },
];

export const FEATURES = [
  { emoji: "🧠", title: "AI-Written Proposals", text: "Professional language that wins clients." },
  {
    emoji: "📄",
    title: "Legal Contract Included",
    text: "11-clause agreement covering payment, revisions, IP, and kill fees.",
  },
  {
    emoji: "🎯",
    title: "Industry-Specific",
    text: "Tailored for web design, photography, copywriting, video, consulting, and more.",
  },
  {
    emoji: "⚡",
    title: "60-Second Generation",
    text: "From blank form to polished documents faster than writing a single paragraph yourself.",
  },
  { emoji: "📥", title: "PDF Export", text: "Download both documents, print, or email directly." },
  {
    emoji: "🔒",
    title: "Your Documents, Saved",
    text: "Access your full history anytime from your personal dashboard.",
  },
];

function Index() {
  return (
    <SiteShell>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[500px]"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 0%, color-mix(in oklab, var(--primary) 12%, transparent), transparent)",
          }}
        />
        <div className="mx-auto max-w-4xl px-6 pt-20 pb-12 text-center sm:pt-28">
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Free AI for freelancers
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Professional Proposals & Contracts in 60 Seconds
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
            DealDraft is an online tool that generates professional proposals and contracts for
            freelancers.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Stop losing clients to slow follow-up. DealDraft writes your proposal and contract the
            moment you close the call — free, polished, and ready to send.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <GenerateCta size="lg" className="h-12 rounded-lg px-7 text-base">
              Generate Your First Proposal Free <ArrowRight className="h-4 w-4" />
            </GenerateCta>
            <p className="text-xs text-muted-foreground">
              Free forever · No credit card · 2 min setup
            </p>
          </div>

          {/* Mockup */}
          <div className="relative mx-auto mt-14 max-w-2xl">
            <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-primary/15 to-accent/15 blur-2xl" />
            <div className="relative">
              <DocMockup
                badge="Client Proposal"
                badgeClass="bg-primary/10 text-primary"
                rotate="-rotate-2"
                offset=""
              />
              <DocMockup
                badge="Service Contract"
                badgeClass="bg-accent/15 text-accent"
                rotate="rotate-2"
                offset="absolute right-0 top-6 hidden sm:block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="bg-secondary/50 py-10">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <p className="text-sm text-muted-foreground">Trusted by freelancers in:</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-medium text-foreground/70">
            <span>🎨 Web Design</span>
            <span>✍️ Copywriting</span>
            <span>📷 Photography</span>
            <span>🎬 Video Production</span>
            <span>💼 Consulting</span>
            <span>💻 Development</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          Three Steps to a Winning Proposal
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-xl border border-border bg-card p-7 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="text-3xl">{s.emoji}</div>
                <div className="text-xs font-semibold text-muted-foreground">Step {i + 1}</div>
              </div>
              <h3 className="text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
            Everything a Freelancer Needs to Get Paid
          </h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="text-2xl">{f.emoji}</div>
                <h3 className="mt-3 text-base font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document preview */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          What You Get Every Time
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <PreviewPanel
            badge="Client Proposal"
            badgeClass="bg-primary/10 text-primary"
            lines={[
              "INTRODUCTION",
              "Hi Sarah — thanks for the call earlier this week. Based on…",
              "",
              "PROJECT UNDERSTANDING",
              "You're launching a refreshed brand identity for…",
            ]}
          />
          <PreviewPanel
            badge="Service Contract"
            badgeClass="bg-accent/15 text-accent"
            lines={[
              "FREELANCE SERVICES AGREEMENT",
              "This agreement is entered into as of March 12, 2026…",
              "",
              "1. PROJECT SCOPE AND DELIVERABLES",
              "Freelancer agrees to deliver: brand strategy doc, logo…",
            ]}
          />
        </div>
        <div className="mt-10 text-center">
          <GenerateCta variant="outline" size="lg">
            Generate your actual documents in 60 seconds →
          </GenerateCta>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
            Simple, Honest Pricing
          </h2>
          <p className="mt-3 text-center text-muted-foreground">
            Start free. Upgrade only when you need unlimited documents.
          </p>
          <PricingCards />
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-20">
        <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          Common Questions
        </h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f) => (
            <AccordionItem key={f.q} value={f.q}>
              <AccordionTrigger className="text-left text-base font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-4xl px-6 pb-20 text-center">
        <div className="rounded-2xl bg-gradient-to-br from-primary to-[oklch(0.466_0.207_268)] p-10 text-primary-foreground sm:p-14">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Your next proposal is 60 seconds away.
          </h2>
          <p className="mt-3 text-primary-foreground/80">
            Free to start. No credit card. No excuses.
          </p>
          <div className="mt-7">
            <GenerateCta
              size="lg"
              variant="secondary"
              className="h-12 rounded-lg px-7 text-base text-primary"
            >
              Generate My Proposal Now →
            </GenerateCta>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function DocMockup({
  badge,
  badgeClass,
  rotate,
  offset,
}: {
  badge: string;
  badgeClass: string;
  rotate: string;
  offset: string;
}) {
  return (
    <div
      className={`${offset} ${rotate} mx-auto w-full max-w-sm rounded-xl border border-border bg-card p-5 text-left shadow-xl transition-transform`}
    >
      <span
        className={`mb-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
      >
        {badge}
      </span>
      <div className="space-y-2">
        <div className="h-3 w-2/3 rounded bg-foreground/80" />
        <div className="h-2 w-full rounded bg-muted" />
        <div className="h-2 w-11/12 rounded bg-muted" />
        <div className="h-2 w-10/12 rounded bg-muted" />
        <div className="h-2 w-9/12 rounded bg-muted" />
        <div className="h-2 w-11/12 rounded bg-muted" />
        <div className="h-2 w-8/12 rounded bg-muted" />
      </div>
    </div>
  );
}

export function PreviewPanel({
  badge,
  badgeClass,
  lines,
}: {
  badge: string;
  badgeClass: string;
  lines: string[];
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-7 shadow-sm">
      <span
        className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
      >
        {badge}
      </span>
      <div className="relative mt-5 space-y-2 text-sm">
        {lines.map((l, i) => (
          <p key={i} className={l === "" ? "h-2" : i < 2 ? "font-medium" : "text-muted-foreground"}>
            {l}
          </p>
        ))}
        <div className="relative">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="my-2 h-2 rounded bg-muted blur-[1.5px]"
              style={{ width: `${95 - i * 6}%` }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-card" />
        </div>
      </div>
    </div>
  );
}

export function PricingCards() {
  return (
    <div className="mt-12 grid gap-6 md:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-7 shadow-sm">
        <div className="text-sm font-semibold text-muted-foreground">Free</div>
        <div className="mt-2 text-4xl font-bold tracking-tight">
          $0<span className="text-base font-normal text-muted-foreground"> / month</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Perfect to try DealDraft and see if it fits your workflow.
        </p>
        <ul className="mt-6 space-y-2.5 text-sm">
          {[
            "3 proposals/contracts per month",
            "AI-powered generation",
            "PDF download",
            "All project types",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-accent" /> {t}
            </li>
          ))}
          {["Document storage", "E-Signature", "Unlimited generations", "Priority support"].map(
            (t) => (
              <li key={t} className="flex items-start gap-2 text-muted-foreground">
                <X className="mt-0.5 h-4 w-4" /> {t}
              </li>
            ),
          )}
        </ul>
        <GenerateCta variant="outline" className="mt-7 w-full">
          Get Started Free
        </GenerateCta>
      </div>

      <div className="relative rounded-2xl border-2 border-primary bg-card p-7 shadow-lg">
        <span className="absolute -top-3 left-7 rounded-full bg-accent px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-accent-foreground">
          Most Popular
        </span>
        <div className="text-sm font-semibold text-muted-foreground">Pro</div>
        <div className="mt-2 text-4xl font-bold tracking-tight">
          $19<span className="text-base font-normal text-muted-foreground"> / month</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          For freelancers who send proposals regularly and want no limits.
        </p>
        <ul className="mt-6 space-y-2.5 text-sm">
          {[
            "Unlimited generations",
            "Document storage — access anytime",
            "E-Signature (coming soon)",
            "AI proposal + contract included",
            "PDF download",
            "Priority support",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-accent" /> {t}
            </li>
          ))}
        </ul>
        <ProUpgradeButton />
        <p className="mt-3 text-center text-xs text-muted-foreground">Cancel anytime</p>
      </div>
    </div>
  );
}

function ProUpgradeButton() {
  const { user, session } = useAuth();
  const checkout = useServerFn(createCheckoutSession);

  async function onUpgrade() {
    try {
      const headers = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined;
      const { url } = await checkout({
        data: { origin: window.location.origin },
        headers,
      });
      window.location.href = url;
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (user) {
    return (
      <Button className="mt-7 w-full" onClick={onUpgrade}>
        Upgrade to Pro — $19/month
      </Button>
    );
  }

  return (
    <Button asChild className="mt-7 w-full">
      <Link to="/signup">Start Pro — $19/mo</Link>
    </Button>
  );
}
